import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/** Contractul lui GET /api/v2/shell (v4/api, App\Shell\Shell::describe). */

export interface Named {
  id: number
  name: string
}

/**
 * Un client din panoul de schimbat firma, cu sigla lui.
 *
 * Adresa siglei o face serverul (`Shell::logo()`), fiindca e ID-ul criptat cu
 * cheia veche a portalului; sirul gol inseamna „nu se deseneaza".
 */
export interface CompanyOption extends Named {
  logo: string
}

export interface ShellModule {
  slug: string
  name: string
  icon: string
  url: string
  tagline: string
}

/** O insigna A/U de pe intrarea Changes (header_menu/*.php). */
export interface NavBadge {
  cls: string
  mark: string
  value: number
  title: string
}

export interface NavItem {
  label: string
  title: string
  href: string
  badge: number
  /** Clasa legaturii (`danger` pe Unassigned). */
  cls?: string
  id?: string
  /** Cifra se scrie si cand e zero (Unassigned in Opteemum). */
  badgeAlways?: boolean
  /** Insigne scrise INAINTEA numelui. */
  badges?: NavBadge[]
  /**
   * Inceputurile de adresa pe care intrarea se aprinde, pe langa `href`-ul ei
   * exact - bara de Settings: o zona (Global, General, Policies) acopera
   * zece-treisprezece ecrane, si `href` duce doar la primul. Cel mai lung
   * prefix castiga (`settings/ai_agent/company` peste `settings/ai_agent`).
   */
  prefixes?: string[]
}

/**
 * Intrarea aprinsa pentru adresa curenta: intai cea cu `href` exact, apoi cea
 * al carei prefix (cel mai lung) acopera adresa - ca `SettingsMenu::zoneFor`.
 */
export function selectedNavItem(nav: readonly NavEntry[], currentHref: string): NavItem | null {
  const exact = nav.find((i): i is NavItem => isItem(i) && i.href.replace(/\/+$/, '') === currentHref)
  if (exact) return exact

  const path = currentHref.toLowerCase()
  let best: NavItem | null = null
  let bestLen = -1
  for (const entry of nav) {
    if (!isItem(entry)) continue
    for (const raw of entry.prefixes ?? []) {
      const prefix = raw.toLowerCase().replace(/^\/+|\/+$/g, '')
      if (prefix === '') continue
      if ((path === prefix || path.startsWith(prefix + '/')) && prefix.length > bestLen) {
        best = entry
        bestLen = prefix.length
      }
    }
  }
  return best
}

export interface NavSpace {
  space: true
}

/** Butonul de ecran intreg din dreapta barei (Proactivitee). */
export interface NavFullscreen {
  fullscreen: true
}

export type NavEntry = NavItem | NavSpace | NavFullscreen

export function isSpace(entry: NavEntry): entry is NavSpace {
  return 'space' in entry
}

export function isFullscreen(entry: NavEntry): entry is NavFullscreen {
  return 'fullscreen' in entry
}

export function isItem(entry: NavEntry): entry is NavItem {
  return 'href' in entry
}

/** Modulele care au deja bara lor in v4 (App\Shell\Shell::MODULES_WITH_NAV). */
export const MODULES = ['overview', 'inventoree', 'softsee', 'opteemum', 'proactivitee', 'securitee', 'protectee', 'printeeng', 'saveenergee', 'productivitee', 'easeedesk', 'settings'] as const
export type Module = (typeof MODULES)[number]

export function isModule(value: string): value is Module {
  return (MODULES as readonly string[]).includes(value)
}

/**
 * Ecranele al caror prim segment NU e numele modulului lor.
 *
 * In portal, `header.php` alege banda dupa `uri->segment(1)`, si are pentru
 * fiecare din astea cate un rand scris de mana (`segment(1) ==
 * 'service_shop'` incarca `header_menu/easeedesk`). Aici e aceeasi lista.
 *
 * `self_service` si ecranele din `easeedesk-dashboards/` n-au randul lor in
 * `header.php`, deci in portal apar fara banda - un gol, nu o hotarare. Aici
 * primesc banda careia le apartin: fara ea, `moduleOf` cadea pe `inventoree`
 * si omul vedea meniul altui modul.
 */
const MODULE_BY_FIRST_SEGMENT: Record<string, Module> = {
  dashboard: 'overview',
  /*
   * `/cmdb` poarta banda OVERVIEW, si o poarta INTOTDEAUNA.
   *
   * In portal e scris in trei locuri, toate trei fara nicio conditie:
   * `Cmdb::__construct()` isi pune `appModule = 'overview'`, `header.php`
   * incarca `header_menu/overview` pentru orice `segment(1) == 'cmdb'`, iar
   * `Navigation\LastPage::MODULE_BY_PATH` trece `cmdb` la `overview`. Intrarea
   * din bara - "CMDB Database" - e in meniul Overview si numai acolo; ecranul
   * si adresa n-au fost atinse cand s-a mutat, fiindca spre `/cmdb` trimit
   * legaturi scrise in patru module.
   *
   * NU e ca la Changes, unde modulul se alege dupa USA (`ADM_MODULE_BY_SCOPE`).
   * Acolo exista cinci intrari in cinci bare, fiecare spre alta ingustare a
   * aceleiasi adrese - `/adm/changes/hardware` din Inventoree, `/software` din
   * Softsee - deci usa se citeste din adresa. Aici intrarea e una singura, iar
   * ce vine din Inventoree, Softsee sau Easeedesk sunt legaturi catre FISA unui
   * element (`/cmdb/view/{id}`): n-au usa in adresa, si portalul nici nu le da
   * una. O usa inventata aici ar insemna o adresa noua, si ar desparti
   * rescrierea de portal fix in locul in care omul s-a plans ca difera.
   *
   * Fara randul asta, `cmdb` nu e numele niciunui modul si `moduleOf` cadea pe
   * `inventoree`: ecranul deschis din Overview se desena cu bara, numele si
   * culorile lui Inventoree.
   */
  cmdb: 'overview',
  /* Pagina de lansare RustDesk: `Remote_connect::rustdesk_launch()` isi
     punea `appModule = 'opteemum'`, deci poarta banda Opteemum. */
  remote_connect: 'opteemum',
  /* `/dashboards/onsite_presence` (prezenta tehnicienilor) e sub dreptul
     Proactivitee si sta in bara lui; modulul `dashboards` din portal
     (tablourile AmCharts vechi) nu mai exista. */
  dashboards: 'proactivitee',
  /*
   * `/maintenance` poarta tot banda OVERVIEW, si din acelasi motiv.
   *
   * In portal, `Maintenance::__construct()` isi pune `appModule = 'overview'`,
   * cu un comentariu care spune de ce: „Merge cu CMDB, in Overview - sunt
   * aceeasi intrare cu trei ecrane". Fara randul asta, `maintenance` nu e
   * numele niciunui modul si `moduleOf` ar cadea pe `inventoree` - a patra fila
   * din banda CMDB s-ar deschide sub bara, numele si culorile altui modul.
   */
  maintenance: 'overview',
  /*
   * `/problems` poarta tot banda OVERVIEW: in portal,
   * `Problems::__construct()` isi pune `appModule = 'overview'`, fiindca acolo
   * e locul in care CMDB-ul isi plateste intretinerea - langa CMDB si Changes,
   * nu langa tichete.
   *
   * Fara randul asta, `problems` nu e numele niciunui modul si `moduleOf` cade
   * pe `inventoree`: lista, fisa si tiparele s-ar deschide sub bara, numele si
   * culorile altui modul.
   */
  problems: 'overview',
  /* `/applications` - aplicatiile de business sunt CI-uri, langa CMDB, in
     banda Overview (`Shell::overviewNav`, "Applications"). */
  applications: 'overview',
  self_service: 'easeedesk',
  service_shop: 'easeedesk',
  /*
   * `/service_catalog` poarta banda EASEEDESK, nu pe cea a lui Inventoree.
   *
   * Ecranul e sub dreptul Inventoree (`inventoree.service_catalog.*`) si sta in
   * `views/version-3/inventoree/`, dar `Service_catalog::__construct()` isi
   * pune `appModule = 'easeedesk'`, iar `header.php` incarca pentru segmentul
   * asta `header_menu/easeedesk`. Motivul e scris acolo: catalogul e prima fila
   * din banda "IT Service Catalog", iar un ecran care isi schimba paleta cand
   * intri in el din alt modul arata a alt produs.
   */
  service_catalog: 'easeedesk',
  'easeedesk-dashboards': 'easeedesk',
  /*
   * `/app/change_log` - jurnalul deschis din subsolul paginii.
   *
   * In portal, controllerul adresei scurte isi pune `appModule = 'change log'`,
   * un nume care nu e al niciunui modul: bara de sus se deseneaza, dar nu se
   * aprinde nimic in ea. Aici nu exista asa ceva - `moduleOf` intoarce
   * INTOTDEAUNA un modul, iar ce nu recunoaste cade pe `inventoree`. Fara
   * randul asta, jurnalul apasat din subsolul unui ecran de Easeedesk se
   * deschidea sub bara lui Inventoree.
   *
   * Il ducem la `settings`, unde ii e familia: acelasi ecran sta si sub
   * rotita. RAIL-UL NU VINE CU ASTA. Bara din stanga n-o alege modulul, ci
   * ADRESA: `Shell` intreaba serverul ce zona are `change_log`, iar
   * `SettingsMenu` are jurnalul inscris numai ca `settings/change_log`, deci
   * raspunsul e gol si rail-ul nu se deseneaza. Exact ca in portal, unde
   * `header.php` pune aceeasi intrebare aceleiasi clase.
   */
  change_log: 'settings',
  /*
   * `/app/feature_requests` si `/app/bug_reports` - celelalte doua legaturi din
   * subsol, si aceeasi poveste ca la jurnal.
   *
   * In portal, `Product_feedback` isi pune `appModule = 'feedback'`, un nume
   * care nu e al niciunui modul: nu se aprinde nimic in bara si ecranul nu are
   * rail. Fara randurile astea, `moduleOf` ar cadea pe `inventoree`, si omul
   * care apasa „Bug reports" din subsolul unui tichet ar primi bara lui
   * Inventoree peste un ecran care nu are nimic de-a face cu ea.
   *
   * `settings` NU aduce rail-ul: bara din stanga o alege ADRESA, iar
   * `SettingsMenu` nu are inscrisa niciuna din cele doua - vezi
   * `ShellFooter.test.tsx`, care cere fix asta.
   */
  feature_requests: 'settings',
  bug_reports: 'settings',
  /*
   * `/services_providers/service_providers` - Service Providers, ecranul din
   * zona GLOBAL a setarilor, singurul rand din `SettingsMenu::items()` a carui
   * adresa nu incepe cu `settings/`. In portal, `Services_providers::
   * __construct()` isi pune `appModule = 'settings'`, iar `SettingsMenu::
   * zoneFor('services_providers/...')` il gaseste in Global (`match` e
   * `services_providers`). Fara randul asta, `services_providers` nu e numele
   * niciunui modul si `moduleOf` cadea pe `inventoree`: ecranul se deschidea cu
   * bara, numele si culorile lui Inventoree, fara zonele Global/General/
   * Policies si fara rail-ul Global - desi omul intrase din rotita.
   *
   * Spre deosebire de jurnal si de cele doua legaturi din subsol, AICI rail-ul
   * VINE: `Shell` intreaba serverul ce zona are adresa, iar `SettingsRail::
   * describe` raspunde Global, cu „Service Providers" aprins.
   */
  services_providers: 'settings',
}

/**
 * Filele IMPRUMUTATE: adresa e a unui modul, bara e a altuia.
 *
 * Cheia e adresa INTREAGA, nu primul segment: `inventoree` ramane Inventoree,
 * doar fila asta e a lui Opteemum. La fel spune si portalul, in doua locuri:
 * `Inventoree::endpoint_performance_dashboard()` isi pune `appModule =
 * 'opteemum'` PE METODA - "ecranul asta sta in Opteemum, nu in Inventoree;
 * intrarea din bara a trecut acolo, iar adresa i-a ramas aici" - iar
 * `Services/Navigation/LastPage.php` tine aceeasi pereche in `MODULE_BY_URI`.
 * Aia e autoritatea, nu adresa.
 *
 * NU e ca la Changes, unde modulul il alege usa pe care intri: acolo acelasi
 * ecran se deschide din cinci module si trebuie sa arate bara celui din care ai
 * venit. Aici intrarea e UNA, in bara Opteemum (`header_menu/opteemum.php`), si
 * nu exista alta usa spre ecranul asta.
 */
const MODULE_BY_URI: Record<string, Module> = {
  'inventoree/endpoint_performance_dashboard': 'opteemum',
}

/**
 * ADM: modulul se alege dupa USA, nu dupa primul segment.
 *
 * `/adm/...` nu e numele niciunui modul, iar ecranul Changes se deschide din
 * CINCI. In portal, `Adm::changes()` isi pune `$appModule` din chiar
 * ingustarea din adresa (`$modules[$scope]`), fiindca acelasi ecran deschis
 * din Inventoree arata bara Inventoree - si scria "Softsee" deasupra ei, adica
 * raspundea de doua ori la "unde sunt", si o data gresit.
 *
 * Restul ecranelor din `/adm/` sunt ale Softsee: acolo le tine si bara
 * (`header_menu/softsee.php`).
 */
const ADM_MODULE_BY_SCOPE: Record<string, Module> = {
  '': 'overview',
  hardware: 'inventoree',
  software: 'softsee',
  proactivitee: 'proactivitee',
  opteemum: 'opteemum',
}

/** Primul segment al adresei paginii: `/app/opteemum/alarms` -> opteemum. `/app/dashboard` e Overview. */
export function moduleOf(pathname: string): Module {
  const path = pathname.replace(/^\/app\//, '').replace(/^\//, '')
  const parts = path.split('/')
  const first = parts[0] ?? ''

  /* Filele imprumutate se recunosc dupa adresa intreaga, inaintea segmentului. */
  const borrowed = MODULE_BY_URI[parts.slice(0, 2).join('/')]
  if (borrowed) return borrowed

  if (first === 'adm') {
    if (parts[1] === 'changes') {
      return ADM_MODULE_BY_SCOPE[parts[2] ?? ''] ?? 'softsee'
    }
    return 'softsee'
  }

  const mapped = MODULE_BY_FIRST_SEGMENT[first]
  if (mapped) return mapped
  return isModule(first) ? first : 'inventoree'
}

export interface ShellData {
  version: string
  user: { id: number; email: string }
  company: { id: number; name: string; logo: string }
  companies: CompanyOption[]
  offices: Named[]
  /**
   * Sediile TUTUROR clientilor, pe cheia firmei - `data-offices` din
   * `_partials/change_company_dropdown.php`. Panoul le tine in pagina: trecerea
   * peste un client trebuie sa-i deschida sediile pe loc, nu dupa o cerere.
   *
   * Cheia vine din JSON, deci e sirul numarului firmei.
   */
  officesByCompany: Record<string, Named[]>
  officeId: number
  allCompanies: boolean
  modules: ShellModule[]
  /** 'C' sau 'F' - `appTemperature` din header.php. */
  temperature: 'C' | 'F'
  module: string
  nav: Partial<Record<Module, NavEntry[]>>
  settings: boolean
  /**
   * Iconita de Agent Releases din bara de sus: numai pentru cutiile postale
   * din `can_manage_unix_agent_releases()` (`Tiers::isGlobalAdmin`). Cand
   * lipseste sau e `false`, elementul nu se scrie deloc - ca in `header.php`.
   */
  canPublishAgents?: boolean
  /**
   * Cate sedii ale firmei curente se cheama inca "Office N" (create de
   * `it_office_auto_create`). 0 sau lipsa pentru cine nu poate edita sediile.
   */
  defaultOfficeNames?: number
  /**
   * Descoperirea firmei curente: 1 = neconfigurata (nicio raza activa sau
   * niciun releu cu program), 2 = nicio scanare de retea de 7 zile, 0 / lipsa =
   * nimic de spus sau fara dreptul de a edita Scan scope.
   */
  discoveryAttention?: number
  /**
   * Cate aplicatii de business ale firmei curente asteapta: de verificat,
   * incomplete sau cu reverificarea venita (banda din Overview > Applications).
   * 0 / lipsa pentru cine n-are `inventoree.cmdb.read`.
   */
  appsAttention?: number
  /**
   * Versiunile de calendar, cea mai noua intai - pentru insigna de pe
   * "Change Log" din subsol. Cate n-a vazut omul se socoteste in browser, din
   * `localStorage`; vezi `App\Shell\Shell::changeLogVersions()`.
   */
  changeLogVersions?: string[]
  /**
   * Are omul Adam Intop (`Assistant::reachable`: dreptul de pe capete si macar
   * o treapta de gandire)? Fals sau lipsa = nu se deseneaza sertarul, bula de
   * pe bara de jos si nici "Ask the agent". Implicit nimeni.
   */
  adamAccess?: boolean
}

export function shellQuery(module: Module) {
  return queryOptions({
    queryKey: ['shell', module],
    queryFn: () => apiGet<ShellData>(`/shell?module=${module}`),
    staleTime: 5 * 60 * 1000,
  })
}
